/**
 * @name AuthContext.js
 * @author 김권영
 * @since 2025. 01. 06.
 * @description
 * 기본 로그인 정보 및 권한
 * 
 * @param {string} compCode - 사업장 코드 
 * @param {string} isAuthenticated - 인증 인가 여부
 * @param {string} role - 권한 이름 (ROLE_ADMIN | ROLE_USER)
 * @param {string} token - JWT 토큰
 * @param {string} userId - 로그인 유저 아이디
 * @param {string} name - 로그인 유저 이름
 */
import React, { createContext, useState, useEffect, useCallback } from 'react';
import { setLogoutCallback } from '../utils/axios_api';
import axiosInstance from '../utils/axios_api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';

export const AuthContext = createContext();

const AuthProvider = ({ children }) => {
    const [auth, setAuth] = useState({
        isAuthenticated: false,
        role: null,
        roleId: null,
        userId: null,
        name: null,
        compCode: null,
        compName: null,
        permissions: [],    // 세분화 권한 코드 배열 (예: ["USER_READ", "COMPANY_WRITE"])
        forceChangePw: 'N',
    });


    const [isSessionExpired, setIsSessionExpired] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    
    
    const loadAuthState = useCallback(() => {
        const authStatus = localStorage.getItem('isAuthenticated') === 'true';
        const role = localStorage.getItem('userRole');
        const roleId = localStorage.getItem('roleId');
        const userId = localStorage.getItem('userId');
        const token = localStorage.getItem('jwt');
        const name = localStorage.getItem('name');
        const compCode = localStorage.getItem('compCode');
        const compName = localStorage.getItem('compName');
        const permissionsRaw = localStorage.getItem('permissions');
        const permissions = permissionsRaw ? JSON.parse(permissionsRaw) : [];
        const forceChangePw = localStorage.getItem('forceChangePw') || 'N';

        setAuth({
            isAuthenticated: authStatus && !!token,
            role,
            roleId,
            userId,
            name,
            compCode,
            compName,
            permissions,
            forceChangePw,
        });
        setIsLoading(false);
    }, []);

    useEffect(() => {
        loadAuthState();
    }, [loadAuthState]);

    // 앱 초기 로드 시 이미 로그인된 상태라면 DB에서 forceChangePw 최신값 동기화
    // (관리자가 비밀번호 초기화 후 해당 계정이 아직 로그아웃 안 한 경우 감지)
    // isLoading이 false로 바뀌는 순간(= 앱 초기화 완료)에만 1회 실행
    useEffect(() => {
        if (!isLoading && auth.isAuthenticated) {
            refreshForceChangePw();
        }
    }, [isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

    const login = (loginInfo) => {
        const permissions = loginInfo.permissions || [];

        localStorage.setItem('userRole', loginInfo.role);
        localStorage.setItem('roleId', loginInfo.roleId);
        localStorage.setItem('userId', loginInfo.userId);
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('jwt', loginInfo.token);
        localStorage.setItem('name', loginInfo.name);
        localStorage.setItem('compCode', loginInfo.compCode);
        localStorage.setItem('compName', loginInfo.compName || "");
        localStorage.setItem('permissions', JSON.stringify(permissions));
        localStorage.setItem('forceChangePw', loginInfo.forceChangePw || 'N');

        setAuth({
            isAuthenticated: true,
            role: loginInfo.role,
            roleId: loginInfo.roleId,
            userId: loginInfo.userId,
            name: loginInfo.name,
            compCode: loginInfo.compCode,
            compName: loginInfo.compName || "",
            permissions,
            forceChangePw: loginInfo.forceChangePw || 'N',
        });

        setIsSessionExpired(false);
    };

    const logout = useCallback((sessionExpired = false) => {

        queryClient.clear();

        localStorage.removeItem('userRole');
        localStorage.removeItem('roleId');
        localStorage.removeItem('userId');
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('jwt');
        localStorage.removeItem('name');
        localStorage.removeItem('compCode');
        localStorage.removeItem('compName');
        localStorage.removeItem('permissions');
        localStorage.removeItem('forceChangePw');
        sessionStorage.removeItem('siteSelection');
        setAuth({
            isAuthenticated: false,
            role: null,
            roleId: null,
            userId: null,
            name: null,
            compCode: null,
            compName: null,
            permissions: [],
            forceChangePw: 'N',
        });


        if (sessionExpired) {
            setIsSessionExpired(true);
            navigate('/'); 
            toast('세션이 만료되었습니다. 다시 로그인해 주세요.');
        } else {
            navigate('/');
            toast('로그아웃 되었습니다.');
        }

    }, [navigate, queryClient]);


    useEffect(() => {
        setLogoutCallback(logout); // Axios 인터셉터에 로그아웃 함수 전달
    }, [logout]);

    /** 비밀번호 강제 변경 완료 후 forceChangePw 플래그 초기화 */
    const clearForceChangePw = useCallback(() => {
        localStorage.setItem('forceChangePw', 'N');
        setAuth((prev) => ({ ...prev, forceChangePw: 'N' }));
    }, []);

    /** DB에서 현재 사용자의 최신 forceChangePw를 조회하여 auth 상태 갱신
     *  관리자가 이미 로그인된 계정의 비밀번호를 초기화한 경우 감지용 */
    const refreshForceChangePw = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/auth/force-change-pw-status');
            const forceChangePw = response.data?.result?.forceChangePw || 'N';
            localStorage.setItem('forceChangePw', forceChangePw);
            setAuth(prev => ({ ...prev, forceChangePw }));
        } catch (e) {
            // 조회 실패 시 기존 상태 유지
        }
    }, []);

    /** 회원정보 수정 후 프로필(이름 등) 즉시 반영 */
    const updateProfile = useCallback(({ name }) => {
        if (name !== undefined) localStorage.setItem('name', name);
        setAuth((prev) => ({ ...prev, ...(name !== undefined && { name }) }));
    }, []);

    /** 세분화 권한 코드 보유 여부 확인 유틸 */
    const hasPermission = (permCode) => {
        return auth.permissions?.includes(permCode) ?? false;
    };

    /**
     * DB에서 현재 사용자의 최신 권한을 조회하여 auth.permissions 갱신합니다.
     * 재로그인 없이 권한 변경을 즉시 반영하기 위해 사용합니다.
     */
    const refreshPermissions = useCallback(async () => {
        if (!auth.isAuthenticated) return;
        try {
            const response = await axiosInstance.get('/user/member/permissions');
            const permissions = response.data ?? [];
            localStorage.setItem('permissions', JSON.stringify(permissions));
            setAuth(prev => ({ ...prev, permissions }));
        } catch (e) {
            console.error('[AuthContext] 권한 갱신 실패:', e);
        }
    }, [auth.isAuthenticated]);

    return (
        <AuthContext.Provider value={{ auth, login, logout, isSessionExpired, isLoading, hasPermission, refreshPermissions, refreshForceChangePw, clearForceChangePw, updateProfile }}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;
